import { Button, Stack, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { Link as RouterLink, useNavigate } from 'react-router'
import { api, isApiError } from '~/api/client'
import { useFetchRegistration } from '~/api/hooks'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { ArtHeading } from '~/components/member/Illustration'
import { MemberLayout } from '~/components/member/layout'
import { Rich, useT } from '~/i18n/use-t'
import { readDraft } from '~/lib/draft'
import { pathForStatus } from '~/lib/flow'
import { useHashParams } from '~/lib/hash'

/**
 * The page behind the "Confirm my email" button of the code email (`/join/confirm#code_id=…&code=…`). Opening it
 * consumes nothing: only pressing the button posts the code (R23, mail programs open links by themselves).
 */
export default function ConfirmEmail() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const params = useHashParams()
  const refetchRegistration = useFetchRegistration()
  const codeId = params.get('code_id')
  const code = params.get('code')
  const email = readDraft().email

  const confirm = useMutation({
    mutationFn: async () => {
      await api('registrationConfirm', { body: { codeId: codeId!, code: code! } })
      // The answer set the registration cookie: read the registration to know the provider and the step.
      return refetchRegistration()
    },
    onSuccess: (reg) => navigate(pathForStatus(reg), { state: { confirmed: true } }),
  })

  const badLink = !codeId || !code || (confirm.isError && isApiError(confirm.error) && confirm.error.status < 500)
  return (
    <MemberLayout>
      <Stack gap={6}>
        <ArtHeading title={t('confirm.title')} art='email' />
        {badLink ? (
          <>
            <Text role='alert'>{t('confirm.oldLink')}</Text>
            <ActionBar>
              <Button asChild size='lg' w='full'>
                <RouterLink to='/join'>{t('confirm.goBack')}</RouterLink>
              </Button>
            </ActionBar>
          </>
        ) : (
          <>
            <Text>{email ? <Rich k='confirm.pressFor' values={{ email }} /> : t('confirm.pressForNoAddress')}</Text>
            {confirm.isError && <ErrorAlert>{friendly(confirm.error)}</ErrorAlert>}
            <ActionBar>
              <Button size='lg' w='full' loading={confirm.isPending} onClick={() => confirm.mutate()}>
                {t('confirm.button')}
              </Button>
            </ActionBar>
          </>
        )}
      </Stack>
    </MemberLayout>
  )
}
